import is from '@sindresorhus/is';
import type { JSX, ReactElement, ReactNode } from 'react';
import { isValidElement, useState } from 'react';

import { Button, type ButtonProps } from '../../core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../core/dropdown-menu/DropdownMenu';

export type Language<T extends string> = {
    readonly id: T;
    readonly name: ReactNode;
};

type LanguagePickerButtonProps = Omit<ButtonProps, 'children' | 'disabled'> & {
    readonly 'data-testid'?: string;
};

type LanguagePickerProps<T extends string> = {
    readonly activeText: string | undefined;
    readonly buttonProps?: LanguagePickerButtonProps;
    readonly disabled?: boolean;
    readonly isGenerating?: boolean;
    readonly languages: readonly Language<T>[];
    readonly onLanguageChange: (languageId: T) => void;
    readonly trigger?: ReactElement;
};

export const LanguagePicker = <T extends string>({
    activeText,
    buttonProps,
    disabled = false,
    isGenerating = false,
    languages,
    onLanguageChange,
    trigger,
}: LanguagePickerProps<T>): JSX.Element => {
    const isButtonDisabled = disabled || isGenerating;
    const [isOpen, setIsOpen] = useState(false);
    const defaultButtonProps: LanguagePickerButtonProps = {
        'data-testid': 'language-picker-button',
        size: 'sm',
        variant: 'secondary',
    };

    return (
        <div className="shrink-0 w-fit">
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                {isValidElement(trigger) ? (
                    <DropdownMenuTrigger disabled={isButtonDisabled}>{trigger}</DropdownMenuTrigger>
                ) : (
                    <DropdownMenuTrigger disabled={isButtonDisabled}>
                        <Button {...defaultButtonProps} {...buttonProps} disabled={isButtonDisabled} isActive={isOpen}>
                            {is.string(activeText) ? activeText : <span className="h-xxl w-[1.875rem]" />}
                        </Button>
                    </DropdownMenuTrigger>
                )}
                <DropdownMenuContent align="end" className="min-w-0 w-fit" sideOffset={5}>
                    {languages.map((language) => (
                        <DropdownMenuItem key={language.id} onSelect={() => onLanguageChange(language.id)}>
                            <div className="flex items-center gap-s justify-between w-full">
                                <span className="flex-[0_1_auto] min-w-0 w-full">{language.name}</span>
                            </div>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
};
